import { useState, useEffect, useRef } from "react";
import {
  getTestimonials, getTransformations, getVideoTestimonials, getSiteSettings,
  adminCreate, adminUpdate, adminDelete, adminUpload, adminSiteSettingsUpdate,
  adminSendDigest, adminPreviewDigest,
} from "../lib/api";
import { Loader2, Plus, Pencil, Trash2, Upload, X, Star, Save, Image as ImageIcon, Video, Mail, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Build absolute URL for uploaded assets so <img>/<video> resolve correctly on the marketing site.
const absoluteUrl = (u) => (!u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`));

const inputCls = "w-full bg-secondary border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-accent transition-colors";
const labelCls = "block text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5";
const btnPrimary = "inline-flex items-center gap-2 rounded-full bg-accent text-white text-sm font-semibold px-5 py-2.5 hover:bg-accent/90 disabled:opacity-60";
const btnGhost = "inline-flex items-center gap-2 rounded-full glass text-sm font-medium px-4 py-2 hover:text-accent";

// -------------- Shared: file/URL input --------------
function AssetInput({ value, onChange, accept = "image/*", kind = "image", testid = "asset" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setUploading(true);
    try {
      const res = await adminUpload(f);
      onChange(absoluteUrl(res.url));
    } catch (e2) {
      setErr(e2.message || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={kind === "video" ? "Paste video URL or upload" : "Paste image URL or upload"}
          className={inputCls} data-testid={`${testid}-url`}
        />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className={`${btnGhost} shrink-0`} data-testid={`${testid}-upload-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading" : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={handleFile} data-testid={`${testid}-file-input`} />
      </div>
      {err && <p className="text-xs text-red-400 mt-2">{err}</p>}
      {value && (
        <div className="mt-3 rounded-xl overflow-hidden border border-border max-w-[240px]">
          {kind === "video"
            ? <video src={value} className="w-full h-auto" controls muted playsInline />
            : <img src={value} alt="preview" className="w-full h-auto object-cover" />}
        </div>
      )}
    </div>
  );
}

// -------------- Testimonials manager --------------
const emptyTest = { name: "", role: "", text: "", rating: 5, avatar: "", order: 0 };

export function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {new: bool, data}
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); getTestimonials().then((d) => setItems(d)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.new) await adminCreate("testimonials", editing.data);
      else await adminUpdate("testimonials", editing.data.id, editing.data);
      setEditing(null); load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await adminDelete("testimonials", id); load();
  };

  return (
    <div data-testid="cms-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-black text-xl">Testimonials</h2>
          <p className="text-sm text-muted-foreground">Text reviews shown on Home and Testimonials pages.</p>
        </div>
        <button onClick={() => setEditing({ new: true, data: { ...emptyTest, order: items.length } })}
          className={btnPrimary} data-testid="testimonial-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="glass rounded-2xl p-5" data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-center gap-3 mb-3">
                {t.avatar && <img src={t.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />}
                <div className="flex-1">
                  <p className="font-bold text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
                <div className="flex gap-0.5">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-3 h-3 fill-accent text-accent" />)}</div>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-3 mb-3">"{t.text}"</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ new: false, data: { ...t } })} className={btnGhost} data-testid={`testimonial-edit-${t.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(t.id)} className={`${btnGhost} text-red-400`} data-testid={`testimonial-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal title={editing.new ? "New testimonial" : "Edit testimonial"} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <Field label="Name"><input className={inputCls} value={editing.data.name} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} data-testid="testimonial-name" /></Field>
          <Field label="Role / Program"><input className={inputCls} value={editing.data.role} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, role: e.target.value } }))} data-testid="testimonial-role" /></Field>
          <Field label="Testimonial text"><textarea rows={4} className={inputCls} value={editing.data.text} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, text: e.target.value } }))} data-testid="testimonial-text" /></Field>
          <Field label="Rating (1-5)"><input type="number" min={1} max={5} className={inputCls} value={editing.data.rating} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, rating: Number(e.target.value) } }))} data-testid="testimonial-rating" /></Field>
          <Field label="Display order"><input type="number" className={inputCls} value={editing.data.order} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, order: Number(e.target.value) } }))} /></Field>
          <Field label="Client photo">
            <AssetInput testid="testimonial-avatar" value={editing.data.avatar} onChange={(v) => setEditing((s) => ({ ...s, data: { ...s.data, avatar: v } }))} />
          </Field>
        </EditorModal>
      )}
    </div>
  );
}

// -------------- Video testimonials --------------
const emptyVideo = { name: "", role: "", video_url: "", poster_url: "", order: 0 };

export function VideoTestimonialsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); getVideoTestimonials().then((d) => setItems(d)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.new) await adminCreate("video-testimonials", editing.data);
      else await adminUpdate("video-testimonials", editing.data.id, editing.data);
      setEditing(null); load();
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this video testimonial?")) return;
    await adminDelete("video-testimonials", id); load();
  };

  return (
    <div data-testid="cms-video-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-black text-xl">Video Testimonials</h2>
          <p className="text-sm text-muted-foreground">Upload 20-second WhatsApp client review videos.</p>
        </div>
        <button onClick={() => setEditing({ new: true, data: { ...emptyVideo, order: items.length } })}
          className={btnPrimary} data-testid="video-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No video testimonials yet. Click Add to upload your first one.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {items.map((v) => (
            <div key={v.id} className="glass rounded-2xl p-4" data-testid={`video-row-${v.id}`}>
              <div className="aspect-video rounded-xl overflow-hidden bg-black mb-3">
                <video src={v.video_url} poster={v.poster_url || undefined} controls muted playsInline className="w-full h-full object-cover" />
              </div>
              <p className="font-bold text-sm">{v.name}</p>
              <p className="text-xs text-muted-foreground mb-3">{v.role}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ new: false, data: { ...v } })} className={btnGhost} data-testid={`video-edit-${v.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(v.id)} className={`${btnGhost} text-red-400`} data-testid={`video-delete-${v.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal title={editing.new ? "New video testimonial" : "Edit video testimonial"} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <Field label="Client name"><input className={inputCls} value={editing.data.name} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} data-testid="video-name" /></Field>
          <Field label="Role / Result"><input className={inputCls} value={editing.data.role} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, role: e.target.value } }))} data-testid="video-role" /></Field>
          <Field label="Video file (MP4/MOV, ~50MB max)">
            <AssetInput testid="video-video" kind="video" accept="video/mp4,video/quicktime,video/webm"
              value={editing.data.video_url}
              onChange={(v) => setEditing((s) => ({ ...s, data: { ...s.data, video_url: v } }))} />
          </Field>
          <Field label="Poster image (optional)">
            <AssetInput testid="video-poster" kind="image"
              value={editing.data.poster_url}
              onChange={(v) => setEditing((s) => ({ ...s, data: { ...s.data, poster_url: v } }))} />
          </Field>
          <Field label="Display order"><input type="number" className={inputCls} value={editing.data.order} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, order: Number(e.target.value) } }))} /></Field>
        </EditorModal>
      )}
    </div>
  );
}

// -------------- Transformations --------------
const emptyTrans = { name: "", category: "Fat Loss", duration: "", result: "", before: "", after: "", quote: "", composite: false, role: "", order: 0 };
const categories = ["Fat Loss", "Muscle Gain", "Strength", "Body Recomposition"];

export function TransformationsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); getTransformations().then((d) => setItems(d)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.new) await adminCreate("transformations", editing.data);
      else await adminUpdate("transformations", editing.data.id, editing.data);
      setEditing(null); load();
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this transformation?")) return;
    await adminDelete("transformations", id); load();
  };

  return (
    <div data-testid="cms-transformations">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-black text-xl">Transformations</h2>
          <p className="text-sm text-muted-foreground">Before/after client transformations shown on Home & Transformations pages.</p>
        </div>
        <button onClick={() => setEditing({ new: true, data: { ...emptyTrans, order: items.length } })}
          className={btnPrimary} data-testid="transformation-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : (
        <div className="grid md:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="glass rounded-2xl p-4" data-testid={`transformation-row-${t.id}`}>
              <div className="grid grid-cols-2 gap-1 mb-3 aspect-[3/2] rounded-xl overflow-hidden">
                {t.before && <img src={t.before} alt="" className="w-full h-full object-cover" />}
                {t.after && <img src={t.after} alt="" className="w-full h-full object-cover" />}
              </div>
              <p className="font-bold text-sm">{t.name} <span className="text-accent">— {t.result}</span></p>
              <p className="text-xs text-muted-foreground mb-3">{t.category} • {t.duration}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ new: false, data: { ...t } })} className={btnGhost} data-testid={`transformation-edit-${t.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(t.id)} className={`${btnGhost} text-red-400`} data-testid={`transformation-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal title={editing.new ? "New transformation" : "Edit transformation"} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <Field label="Client name"><input className={inputCls} value={editing.data.name} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, name: e.target.value } }))} data-testid="trans-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select className={inputCls} value={editing.data.category} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, category: e.target.value } }))}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Duration (e.g. 45 days)"><input className={inputCls} value={editing.data.duration} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, duration: e.target.value } }))} /></Field>
          </div>
          <Field label="Result headline (e.g. -10 kg)"><input className={inputCls} value={editing.data.result} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, result: e.target.value } }))} /></Field>
          <Field label="Client quote"><textarea rows={3} className={inputCls} value={editing.data.quote} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, quote: e.target.value } }))} /></Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!editing.data.composite} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, composite: e.target.checked } }))} />
            Single composite image (before + after already merged)
          </label>
          <Field label={editing.data.composite ? "Composite image" : "Before image"}>
            <AssetInput testid="trans-before" value={editing.data.before} onChange={(v) => setEditing((s) => ({ ...s, data: { ...s.data, before: v, ...(s.data.composite ? { after: v } : {}) } }))} />
          </Field>
          {!editing.data.composite && (
            <Field label="After image">
              <AssetInput testid="trans-after" value={editing.data.after} onChange={(v) => setEditing((s) => ({ ...s, data: { ...s.data, after: v } }))} />
            </Field>
          )}
          <Field label="Display order"><input type="number" className={inputCls} value={editing.data.order} onChange={(e) => setEditing((s) => ({ ...s, data: { ...s.data, order: Number(e.target.value) } }))} /></Field>
        </EditorModal>
      )}
    </div>
  );
}

// -------------- Site Settings --------------
function DigestCard() {
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const send = async () => {
    setSending(true); setStatus(null);
    try {
      const res = await adminSendDigest();
      setStatus({ ok: true, msg: `Sent • ${res.counts.applications} applications, ${res.counts.contacts} contacts, ${res.counts.orders} orders, ${res.counts.subscribers} subscribers` });
    } catch (e) {
      setStatus({ ok: false, msg: e.message || "Failed to send" });
    } finally { setSending(false); }
  };

  const showPreview = async () => {
    setLoadingPreview(true);
    try { setPreview(await adminPreviewDigest()); } finally { setLoadingPreview(false); }
  };

  return (
    <div className="glass rounded-2xl p-6 mb-8 border border-accent/30" data-testid="digest-card">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5 text-accent" />
        </span>
        <div className="flex-1">
          <h2 className="font-heading font-black text-lg">Weekly Email Digest</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every <span className="text-accent font-semibold">Monday 9:00 AM IST</span> you'll get an email summary of the week's new coaching applications, contacts, orders and subscribers — even if you don't open the dashboard.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={send} disabled={sending} className={btnPrimary} data-testid="digest-send-now">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send digest to me now
            </button>
            <button onClick={showPreview} disabled={loadingPreview} className={btnGhost} data-testid="digest-preview">
              {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Preview this week
            </button>
          </div>
          {status && (
            <p className={`text-xs mt-3 ${status.ok ? "text-accent" : "text-red-400"}`} data-testid="digest-status">{status.msg}</p>
          )}
        </div>
      </div>
      {preview && (
        <div className="mt-5 border-t border-border pt-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Preview</p>
          <p className="text-sm mb-3">
            Applications: <b>{preview.counts.applications}</b> • Contacts: <b>{preview.counts.contacts}</b> • Orders: <b>{preview.counts.orders}</b> • Subscribers: <b>{preview.counts.subscribers}</b>
          </p>
          <div className="rounded-xl overflow-hidden border border-border bg-white max-h-[500px] overflow-y-auto">
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteSettingsManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { getSiteSettings().then((d) => setData(d || {})); }, []);

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminSiteSettingsUpdate(data);
      setData(res); setSavedAt(new Date().toLocaleTimeString());
    } finally { setSaving(false); }
  };
  const on = (k) => (v) => setData((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="cms-site-settings" className="max-w-3xl">
      <DigestCard />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-black text-xl">Site Settings</h2>
          <p className="text-sm text-muted-foreground">Hero image, headline copy, founder photo and contact numbers.</p>
        </div>
        <button onClick={save} disabled={saving} className={btnPrimary} data-testid="site-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>
      {savedAt && <p className="text-xs text-accent mb-4">Saved at {savedAt}</p>}

      <div className="space-y-5">
        <Field label={<span className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Hero background image</span>}>
          <AssetInput testid="site-hero-image" value={data.hero_image} onChange={on("hero_image")} />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Hero title (line 1)"><input className={inputCls} value={data.hero_title_1 || ""} onChange={(e) => on("hero_title_1")(e.target.value)} data-testid="site-hero-1" /></Field>
          <Field label="Hero title (line 2)"><input className={inputCls} value={data.hero_title_2 || ""} onChange={(e) => on("hero_title_2")(e.target.value)} data-testid="site-hero-2" /></Field>
        </div>
        <Field label="Hero subtitle"><textarea rows={3} className={inputCls} value={data.hero_subtitle || ""} onChange={(e) => on("hero_subtitle")(e.target.value)} data-testid="site-hero-sub" /></Field>
        <Field label={<span className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Founder photo (About preview)</span>}>
          <AssetInput testid="site-about-image" value={data.about_image} onChange={on("about_image")} />
        </Field>
        <Field label="Founder tagline"><input className={inputCls} value={data.founder_tagline || ""} onChange={(e) => on("founder_tagline")(e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="WhatsApp number (with country code)"><input className={inputCls} value={data.whatsapp_number || ""} onChange={(e) => on("whatsapp_number")(e.target.value)} data-testid="site-whatsapp" /></Field>
          <Field label="UPI ID"><input className={inputCls} value={data.upi_id || ""} onChange={(e) => on("upi_id")(e.target.value)} data-testid="site-upi" /></Field>
        </div>
      </div>
    </div>
  );
}

// -------------- Small helpers --------------
function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function EditorModal({ title, children, onClose, onSave, saving }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cms-editor-modal">
      <div className="bg-background border border-border rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-border bg-background/95 backdrop-blur">
          <h3 className="font-heading font-black text-lg">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="cms-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">{children}</div>
        <div className="sticky bottom-0 p-5 border-t border-border bg-background/95 backdrop-blur flex justify-end gap-3">
          <button onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={onSave} disabled={saving} className={btnPrimary} data-testid="cms-editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export const CMS_ICONS = { Video, ImageIcon };
